import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import Layout from '../ui/Layout';
import Card from '../ui/Card';
import InputField from '../ui/InputField';
import Button from '../ui/Button';
import { useInvoiceSearch } from '../hooks/useInvoiceSearch';
import { UseCaseProvider } from '../../application/useCases/UseCaseProvider';
import './InvoiceSearchPage.css';

const InvoiceSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { invoice, error, loading, captchaValid, searchInvoice, validateCaptcha, resetCaptcha } = useInvoiceSearch();
  const [documentNumber, setDocumentNumber] = useState('');
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  // Prellenar el input desde ?referencia= en la URL (deep-link).
  // Solo se prellena: el usuario debe resolver el reCAPTCHA y presionar Continuar.
  useEffect(() => {
    const referencia = searchParams.get('referencia');
    if (referencia && referencia.trim() !== '') {
      setDocumentNumber(referencia.trim());
      setShowCaptcha(true);
    }
    // Solo al montar: la referencia se lee una vez de la URL inicial.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cuando cambia el invoice (factura), navegamos a la página de detalle de factura
  useEffect(() => {
    if (invoice) {
      navigate(`/invoice/${invoice.id}`, { state: { invoice } });
    }
  }, [invoice, navigate]);

  // Resetear reCAPTCHA cuando hay error
  useEffect(() => {
    if ((error || searchError) && recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  }, [error, searchError]);

  useEffect(() => {
    if (!captchaValid && recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  }, [captchaValid]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setDocumentNumber(value);
    setShowCaptcha(value.trim() !== '');
    setSearchError(null);
  };

  const isRecordatorio = (value: string): boolean => {
    return /^\d+$/.test(value.trim());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!documentNumber.trim() || !captchaValid) return;

    setSearchError(null);

    if (isRecordatorio(documentNumber)) {
      // Búsqueda de recordatorio de pago
      const nro = parseInt(documentNumber.trim(), 10);
      setIsLoading(true);
      try {
        const useCase = UseCaseProvider.getFindRecordatorioUseCase();
        const result = await useCase.execute(nro);
        if (result.success && result.recordatorio) {
          navigate(`/recordatorio/${nro}`, { state: { recordatorio: result.recordatorio } });
        } else {
          setSearchError(result.message || 'No se encontró el recordatorio de pago');
          resetCaptcha();
        }
      } catch {
        setSearchError('Error al procesar la solicitud. Por favor, inténtelo de nuevo.');
        resetCaptcha();
      } finally {
        setIsLoading(false);
      }
    } else {
      // Búsqueda de factura (prefijo-numero)
      searchInvoice(documentNumber.toUpperCase());
    }
  };

  const handleCaptchaChange = (token: string | null) => {
    if (token) {
      validateCaptcha(token);
    } else {
      resetCaptcha();
    }
  };

  const displayError = error || searchError;
  const displayLoading = loading || isLoading;

  return (
    <Layout>
      <Card title="Sistema de Recaudo de Comisiones">
        <form onSubmit={handleSubmit}>
          <InputField
            id="documentNumber"
            label="Número de Factura o Recordatorio de pago"
            tooltip="Para facturas: ingrese el prefijo, guión (-) y número de factura. Ejemplo: FC20-5246. Para recordatorios de pago: ingrese solo números enteros. Ejemplo: 1025."
            placeholder="Ej: FC20-5246 o 1025"
            value={documentNumber}
            onChange={handleInputChange}
            error={displayError || undefined}
            required
          />

          {showCaptcha && (
            <div className="recaptcha-container">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
                onChange={handleCaptchaChange}
              />
            </div>
          )}

          <Button
            type="submit"
            disabled={!captchaValid || displayLoading || !documentNumber.trim()}
            fullWidth
          >
            {displayLoading ? 'Cargando...' : 'Continuar'}
          </Button>
        </form>
      </Card>
    </Layout>
  );
};

export default InvoiceSearchPage; 